import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useState } from 'react';

interface UseVoteOptions {
  postId: string;
  initialVoted?: boolean;
  initialScore?: number;
}

export function useVote({ postId, initialVoted = false, initialScore = 0 }: UseVoteOptions) {
  const queryClient = useQueryClient();
  const [optimisticVoted, setOptimisticVoted] = useState(initialVoted);
  const [optimisticScore, setOptimisticScore] = useState(initialScore);

  const voteMutation = useMutation({
    mutationFn: () => api.votes.toggle(postId),
    onMutate: async () => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['post', postId] });
      await queryClient.cancelQueries({ queryKey: ['posts'] });

      // Optimistically update UI
      const newVoted = !optimisticVoted;
      const newScore = newVoted ? optimisticScore + 1 : optimisticScore - 1;

      setOptimisticVoted(newVoted);
      setOptimisticScore(newScore);

      return { previousVoted: optimisticVoted, previousScore: optimisticScore };
    },
    onError: (_err, _variables, context) => {
      // Revert optimistic update on error
      if (context) {
        setOptimisticVoted(context.previousVoted);
        setOptimisticScore(context.previousScore);
      }
    },
    onSuccess: (data) => {
      // Update with server response
      setOptimisticVoted(data.voted);
      setOptimisticScore(data.score);

      // Invalidate queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: ['post', postId] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['voters', postId] });
    },
  });

  const toggleVote = () => {
    voteMutation.mutate();
  };

  return {
    voted: optimisticVoted,
    score: optimisticScore,
    toggleVote,
    isLoading: voteMutation.isPending,
  };
}
